import { useState, useEffect, useMemo } from 'react';
import Questions from './Questions/Questions';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useHistory } from 'react-router-dom';
import { feedbackService } from 'modules/Feedback/services/feedback';
import './Template.scss';

const isAnswered = value => {
  if (value === undefined || value === null || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

const Template = ({ template, feedbackId = null, feedbackToken = null, hidePageTitle = false }) => {
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const history = useHistory();
  const [answers, setAnswers] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  // Preview templates pass neither; live invite forms pass feedbackToken.
  const liveFeedbackKey = feedbackToken || feedbackId;

  const questions = template?.questions;
  const totalQuestions = questions?.length || 0;
  const answeredCount = useMemo(
    () => (questions || []).filter(question => isAnswered(answers[question.questionId])).length,
    [answers, questions]
  );
  const progressPercent = totalQuestions ? Math.round((answeredCount / totalQuestions) * 100) : 0;

  const reshapeAnswers = answers => {
    return Object.entries(answers).map(([questionId, value]) => ({
      questionId: parseInt(questionId),
      value: typeof value === 'string' ? value : JSON.stringify(value)
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!isFormValid || loading) return;
    const questionsAnswers = reshapeAnswers(answers);
    try {
      setLoading(true);
      setSubmitError(null);
      const response = await feedbackService.createFeedbackResponse(
        feedbackToken,
        questionsAnswers
      );
      history.push(`/response-confirmation`);
      localStorage.removeItem('idToken');
      return response;
    } catch (error) {
      if (error?.response?.status === 403) {
        localStorage.removeItem('idToken');
        setSubmitError('This feedback was already submitted.');
      } else if (error?.response?.status === 401) {
        localStorage.removeItem('idToken');
        history.replace(`/feedback/login?token=${encodeURIComponent(feedbackToken || '')}`);
      } else {
        setSubmitError('Something went wrong while submitting. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prevAnswers => ({
      ...prevAnswers,
      [questionId]: value
    }));
  };

  useEffect(() => {
    const allQuestionsAnswered =
      (questions || []).every(question => isAnswered(answers[question.questionId])) || false;
    setIsFormValid(totalQuestions > 0 && allQuestionsAnswered);
  }, [answers, questions, totalQuestions]);

  return (
    <div className="templateContainer">
      {!hidePageTitle && (
        <h2 className="templateName">
          {liveFeedbackKey ? `Feedback for ${template?.employeeName}` : template?.name}
        </h2>
      )}
      <div className="questionsContainer">
        {liveFeedbackKey && (template?.name || template?.templateName) && (
          <p className="templateSubtitle">{template?.name || template?.templateName}</p>
        )}
        {liveFeedbackKey && totalQuestions > 0 && (
          <div className="formProgress" aria-live="polite">
            <div className="formProgressMeta">
              <span>
                {answeredCount} of {totalQuestions} answered
              </span>
              <span>{progressPercent}%</span>
            </div>
            <div className="formProgressTrack">
              <div className="formProgressFill" style={{ width: `${progressPercent}%` }} />
            </div>
          </div>
        )}
        <form className="formQuestions" onSubmit={handleSubmit}>
          {totalQuestions === 0 ? (
            <p className="emptyQuestions">No questions available for this feedback.</p>
          ) : (
            (questions || []).map((question, index) => (
              <Questions
                key={question.questionId || index}
                index={index + 1}
                question={question}
                feedbackId={liveFeedbackKey}
                handleAnswerChange={handleAnswerChange}
                answers={answers}
              />
            ))
          )}
          {liveFeedbackKey && (
            <div className="submitSection">
              {!isFormValid && totalQuestions > 0 && (
                <p className="submitHint">Answer all questions to enable submit.</p>
              )}
              {submitError && <p className="submitError">{submitError}</p>}
              <Button
                className={loading || !isFormValid ? 'loadingBtn showBtn' : 'showBtn'}
                color="primary"
                type="submit"
                disabled={loading || !isFormValid}
              >
                {loading && (
                  <CircularProgress size={16} color="inherit" className="submitSpinner" />
                )}
                {loading ? 'Submitting…' : 'Submit Feedback'}
              </Button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default Template;
